import { create } from 'zustand';
import { adminDeliveryWindowManagementApi } from '@api/api-config.ts';
import {
  AdminDeliveryWindowDetailResponse,
  AdminDeliveryWindowListResponse,
  AdminSaveDeliveryWindowRequest,
} from '@api/generated/portal-be-admin';

interface Pagination {
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

interface AdminDeliveryWindowState {
  data: AdminDeliveryWindowListResponse[];
  /** Filtry, se kterymi byl seznam nacten — obnova po ulozeni je musi zopakovat. */
  lastOrganizationFilter?: number;
  lastPortalFilter?: number;
  lastSupplierFilter?: number;
  pagination: Pagination | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

interface LoadParams {
  /** Zakaznik i dodavatel jsou volitelne, aspon jeden z nich ale musi byt vyplneny. */
  organizationId?: number;
  portalId?: number;
  supplierId?: number;
  q?: string;
  page?: number;
  size?: number;
}

interface AdminDeliveryWindowActions {
  loadDeliveryWindows: (params: LoadParams) => Promise<void>;
  loadDeliveryWindowDetail: (id: number) => Promise<AdminDeliveryWindowDetailResponse | null>;
  saveDeliveryWindow: (id: number | null, request: AdminSaveDeliveryWindowRequest) => Promise<boolean>;
  deleteDeliveryWindow: (id: number) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

const messageOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export const useAdminDeliveryWindowStore = create<AdminDeliveryWindowState & AdminDeliveryWindowActions>((set, get) => ({
  data: [],
  lastOrganizationFilter: undefined,
  lastPortalFilter: undefined,
  lastSupplierFilter: undefined,
  pagination: null,
  loading: false,
  saving: false,
  error: null,

  loadDeliveryWindows: async ({ organizationId, portalId, supplierId, q, page = 1, size = 20 }) => {
    set({ loading: true, error: null });

    try {
      const response = await adminDeliveryWindowManagementApi.getAdminDeliveryWindows({ organizationId, portalId, supplierId, q, page, size });
      set({
        data: response.data.items,
        lastOrganizationFilter: organizationId,
        lastPortalFilter: portalId,
        lastSupplierFilter: supplierId,
        pagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
        },
        loading: false,
      });
    } catch (err: unknown) {
      set({ error: messageOf(err, 'Nepodařilo se načíst závozová okna'), loading: false, data: [], pagination: null });
    }
  },

  loadDeliveryWindowDetail: async (id: number) => {
    try {
      const response = await adminDeliveryWindowManagementApi.getAdminDeliveryWindowDetail({ id });
      return response.data;
    } catch (err: unknown) {
      set({ error: messageOf(err, 'Nepodařilo se načíst detail závozového okna') });
      return null;
    }
  },

  // id === null zaklada nove okno, jinak edituje stavajici.
  saveDeliveryWindow: async (id: number | null, request: AdminSaveDeliveryWindowRequest) => {
    set({ saving: true, error: null });

    try {
      if (id === null) {
        await adminDeliveryWindowManagementApi.createAdminDeliveryWindow({ adminSaveDeliveryWindowRequest: request });
      } else {
        await adminDeliveryWindowManagementApi.updateAdminDeliveryWindow({ id, adminSaveDeliveryWindowRequest: request });
      }
      set({ saving: false });

      // Seznam se obnovuje se stejnym filtrem, jaky ma uzivatel na obrazovce — ulozene okno v nem byt nemusi
      // (pri filtru na dodavatele se da zalozit okno jinemu zakaznikovi).
      const { pagination, lastOrganizationFilter, lastPortalFilter, lastSupplierFilter } = get();
      const hasFilter = lastOrganizationFilter !== undefined || lastSupplierFilter !== undefined;
      await get().loadDeliveryWindows({
        organizationId: hasFilter ? lastOrganizationFilter : request.organizationId,
        portalId: hasFilter ? lastPortalFilter : undefined,
        supplierId: hasFilter ? lastSupplierFilter : undefined,
        page: pagination?.page ?? 1,
        size: pagination?.size ?? 20,
      });
      return true;
    } catch (err: unknown) {
      set({ error: messageOf(err, 'Nepodařilo se uložit závozové okno'), saving: false });
      return false;
    }
  },

  deleteDeliveryWindow: async (id: number) => {
    try {
      await adminDeliveryWindowManagementApi.deleteAdminDeliveryWindow({ id });
      set(state => ({ data: state.data.filter(window => window.id !== id) }));
      return true;
    } catch (err: unknown) {
      set({ error: messageOf(err, 'Nepodařilo se smazat závozové okno') });
      return false;
    }
  },

  clearError: () => set({ error: null }),

  reset: () =>
    set({
      data: [],
      lastOrganizationFilter: undefined,
      lastPortalFilter: undefined,
      lastSupplierFilter: undefined,
      pagination: null,
      loading: false,
      saving: false,
      error: null,
    }),
}));
